import { Component, OnInit } from '@angular/core';
import { MpdEndpoint, MPDService, MpdStatus } from 'src/app/services/mpd.service';

type ConnectionState = 'idle' | 'connecting' | 'connected' | 'error';

@Component({
  selector: 'app-mpd-tests',
  templateUrl: './mpd-tests.component.html',
  styleUrls: ['./mpd-tests.component.scss']
})
export class MpdTestsComponent implements OnInit {
  public status: MpdStatus | null = null;
  public endpoint: MpdEndpoint | null = null;
  public connectionState: ConnectionState = 'idle';
  public controlInProgress = false;
  public error: string | null = null;
  public lastChecked: Date | null = null;

  constructor(private mpdService: MPDService) {}

  ngOnInit(): void {
    this.connect();
  }

  get isConnected(): boolean {
    return this.connectionState === 'connected' && Boolean(this.status?.connected);
  }

  get isPlaying(): boolean {
    return this.status?.state === 'play';
  }

  get connectionLabel(): string {
    if (this.connectionState === 'connecting') {
      return 'Connecting';
    }

    if (this.connectionState === 'connected') {
      return 'Connected';
    }

    if (this.connectionState === 'error') {
      return 'Disconnected';
    }

    return 'Not checked';
  }

  get playbackLabel(): string {
    if (!this.isConnected) {
      return 'Unavailable';
    }

    if (this.status?.state === 'play') {
      return 'Playing';
    }

    if (this.status?.state === 'pause') {
      return 'Paused';
    }

    return 'Stopped';
  }

  get volumeLabel(): string {
    const volume = this.status?.volume;
    return volume === null || volume === undefined || volume < 0 ? 'Unavailable' : `${volume}%`;
  }

  get playbackProgress(): number {
    const elapsed = this.status?.elapsed ?? 0;
    const duration = this.status?.duration ?? 0;

    if (duration <= 0) {
      return 0;
    }

    return Math.max(0, Math.min(100, (elapsed / duration) * 100));
  }

  public connect(): void {
    if (this.connectionState === 'connecting' || this.controlInProgress) {
      return;
    }

    this.connectionState = 'connecting';
    this.error = null;

    this.mpdService.getStatus().subscribe({
      next: (status) => this.applyStatus(status),
      error: (error) => this.applyError(error)
    });
  }

  public togglePlayback(): void {
    if (!this.isConnected || this.controlInProgress) {
      return;
    }

    this.controlInProgress = true;
    this.error = null;

    this.mpdService.toggle().subscribe({
      next: (status) => {
        this.controlInProgress = false;
        this.applyStatus(status);
      },
      error: (error) => {
        this.controlInProgress = false;
        this.applyError(error);
      }
    });
  }

  public formatTime(seconds: number | null | undefined): string {
    if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
      return '--:--';
    }

    const totalSeconds = Math.max(0, Math.floor(seconds));
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  private applyStatus(status: MpdStatus): void {
    this.status = status;
    this.endpoint = status.endpoint;
    this.connectionState = 'connected';
    this.error = null;
    this.lastChecked = new Date();
  }

  private applyError(error: any): void {
    this.status = null;
    this.endpoint = error?.error?.endpoint ?? this.endpoint;
    this.connectionState = 'error';
    this.error = error?.error?.detail
      ?? error?.error?.error
      ?? error?.message
      ?? 'Unable to connect to Music Player Daemon.';
    this.lastChecked = new Date();
  }
}
